import { EmptyDataWrap } from '@/components/EmptyDataWrap';
import { formatNumberWithTooltip } from '@/components/NumberFormat';
import TotalOverviewTooltip from '@/components/ToolTip/TotalOverviewTooltip';
import { THEME_ENUM } from '@/constants';
import { useTokensInfoConfig } from '@/features/config/hook';
import { useTheme } from '@/features/global/hooks';
import { useFetchTotalVolume } from '@/features/portfolio/query';
import { useChainId, useUserAddr } from '@/hooks/web3/useChain';
import { PieChartItem } from '@/types/chart';
import { ITotalVolume } from '@/types/portfolio';
import { TokenInfoMap } from '@/types/token';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import PieChartWithText from '../../PieChartWithText';
import { disabledDarkColor, disabledLightColor } from '../../constant';
import { PieChartWithTextsProps } from '../types';
export default function TotalVolumeChart({ statusIsLoading }: PieChartWithTextsProps): JSX.Element {
  const userAddr = useUserAddr();
  const chainId = useChainId();
  const { dataTheme } = useTheme();
  const { t } = useTranslation();
  const { data: totalVolume, isLoading: isLoadingTotalVolume } = useFetchTotalVolume(chainId, userAddr);
  const isLoading = useMemo(() => statusIsLoading || isLoadingTotalVolume, [statusIsLoading, isLoadingTotalVolume]);
  {
    /* // @TODO uncomment after build large scale details solution */
  }
  //const [isModalOpen, setIsModalOpen] = useState<boolean>(true);

  const tokensInfoConfig = useTokensInfoConfig(chainId);
  const totalVolumeUSD = useMemo(
    () => formatNumberWithTooltip({ num: totalVolume?.totalVolumeUsd || '-', prefix: '$', isShowTBMK: true }),
    [totalVolume?.totalVolumeUsd],
  );
  const qouteVolumeData = useMemo(() => {
    if (!totalVolume?.details) {
      return [];
    }
    return groupedVolumeDetails(totalVolume, tokensInfoConfig?.marginTokenMap);
  }, [totalVolume, tokensInfoConfig?.marginTokenMap]);
  const volumeChartList: PieChartItem[] = useMemo(() => {
    if (statusIsLoading || Number(totalVolume?.totalVolumeUsd || 0) === 0) {
      return [
        {
          symbol: '',
          name: 'Others',
          value: 1,
          color: dataTheme === THEME_ENUM.DARK ? disabledDarkColor : disabledLightColor, //PAIR_COLOR_TYPE.Others,
          tokenAmount: undefined,
          usdAmount: undefined,
        },
      ];
    }
    return qouteVolumeData;
  }, [statusIsLoading, totalVolume?.totalVolumeUsd, dataTheme, qouteVolumeData]);
  return (
    <>
      {/* // @TODO uncomment after build large scale details solution */}
      {/* <VolumeDetailModal open={isModalOpen} onCancel={() => setIsModalOpen(false)} /> */}

      <PieChartWithText
        tooltipTitle={
          <TotalOverviewTooltip
            id="totalVolume-tooltip"
            title={t('common.portfolio.totalVolume')}
            value={totalVolumeUSD}
            data={[
              {
                type: 'quote',
                items: qouteVolumeData,
              },
            ]}
          />
        }
        size="small"
        valuesPieChartProps={{
          noHoverEffect: true,
          chartList: volumeChartList,
          disabled: isLoading,
          isHollow: false,
        }}
        title={t('common.portfolio.totalVolume')}
        value={
          <EmptyDataWrap isLoading={statusIsLoading}>
            <div className="syn-total-overview-volume">
              {totalVolumeUSD}
              {/* // @TODO uncomment after build large scale details solution */}
              {/* <span onClick={() => setIsModalOpen(true)} className="syn-total-overview-csv-btn">
                <IconEye />
                {t('common.details')}
              </span> */}
            </div>
          </EmptyDataWrap>
        }
      />
    </>
  );
}

function groupedVolumeDetails(totalVolume: ITotalVolume, marginTokenMap?: TokenInfoMap | undefined) {
  const groupedVolumes = totalVolume?.details?.reduce(
    (acc, item) => {
      const symbol = item.quoteToken.symbol;
      if (!acc[symbol]) {
        acc[symbol] = {
          name: symbol,
          color: marginTokenMap?.[item.quoteToken.address]?.color || 'black',
          symbol: symbol,
          tokenAmount: '0',
          usdAmount: '0',
          quoteVolume: 0,
          volumeUsd: 0,
          value: 0,
        };
      }
      acc[symbol].quoteVolume += Number(item.quoteVolume);
      acc[symbol].volumeUsd += Number(item.volumeUsd);
      acc[symbol].tokenAmount = formatNumberWithTooltip({ num: Number(acc[symbol].quoteVolume), isShowTBMK: true });
      acc[symbol].usdAmount = formatNumberWithTooltip({
        num: Number(acc[symbol].volumeUsd),
        prefix: '$',
        isShowTBMK: true,
      });
      acc[symbol].value = Number(acc[symbol].volumeUsd);
      return acc;
    },
    {} as {
      [tokenSymbol: string]: {
        name: string;
        color: string;
        symbol: string;
        tokenAmount: React.ReactNode;
        usdAmount: React.ReactNode;
        quoteVolume: number;
        volumeUsd: number;
        value: number;
      };
    },
  );

  return Object.values(groupedVolumes || {}).filter((item) => item.value > 0);
}
